import React, { useEffect, useMemo, useRef, useState } from 'react'
import { liveTuneStore, DEFAULTS, computeDiff } from '../lib/liveTuneStore'
import { useLiveTune, useCameraCageGizmoVisible } from '../hooks/useLiveTune'
import { getCurrentCharacterId, setCurrentCharacterId, loadCharacters } from '../lib/characterState'
import { listEnvironments } from '../lib/environmentsApi'
import { isHiddenFolderPath, showHiddenFolders } from '../lib/hiddenFolders'
import { resolveCurrentEnvironment } from '../lib/currentEnvironment.js'
import { CustomAssetManager } from '../lib/CustomAssetManager'
import { normalizePose } from '../lib/envPose'
import { toSliderPos, fromSliderPos, quantize } from '../lib/logScale'
import { getAdapter } from '../lib/transformAdapters'
import { sceneSelection } from '../lib/sceneSelection'
import { placeAtView } from '../lib/lightPlacement'
import * as transformHistory from '../lib/transformHistory'
import { isRecordGoneError, STALE_ASSET_MESSAGE } from '../lib/assetErrors'
import { collectTune, getTuneBaseline, setTuneBaseline, applyTuneForEnvironment, extractTuneFromAsset, diffTune, revertTuneKey, discardTuneToBaseline } from '../lib/envTune'
import { subscribe as subscribeTextureQuality } from '../lib/textureQuality'
import { kelvinToHex } from '../lib/colorTemperature'
import { CAMERA_ACCENT } from '../lib/cameraTheme'

// Per-section accent hues, keyed by node category. Only the Camera accent is
// shared (via cameraTheme) — three camera surfaces must agree on it. The rest
// stay inline; there is no shared node-visuals registry to import.
const CATEGORY_COLORS = {
  Character: '#e0a060',
  Camera: CAMERA_ACCENT,
  Debug: '#40d090',
  Scene: '#8a94a8',
  Light: '#e0c060',
  Material: '#c07850',
}

// Space-insensitive, case-insensitive substring matcher for the section
// filter box. "setcamera" matches "Set Camera". Inlined (was paletteSearch).
function matchesQuery(query, fields) {
  const q = String(query ?? '').toLowerCase().replace(/\s+/g, '')
  if (q === '') return true
  const hay = (fields || []).filter(Boolean).join(' ').toLowerCase().replace(/\s+/g, '')
  return hay.includes(q)
}

/**
 * LiveTunePanel — dev-only live-mode tuner.
 *
 * Opt-in via ?tune=1 or localStorage.tune === '1'. Floating top-right
 * panel; styled to match AnimTestPanel. Sections: Environment, Camera,
 * Lighting, Shadow, Debug.
 *
 * Writes to liveTuneStore, which persists to localStorage under
 * aos-livetune-v3 and is consumed by Scene / EnvProbe /
 * ShadowSunLight / ShadowCatcher / App (Canvas FOV). All changes apply live without a
 * reload, except swapping the splat URL which triggers a re-mount.
 */

const PANEL_WIDTH = 300

// Environment scale slider bounds (ENG-895). The numeric field beside the
// slider accepts anything > 0; these only bound the drag range.
const ENV_SCALE_MIN = 0.01
const ENV_SCALE_MAX = 1000

// Foldable groups, top-to-bottom. Ids key the persisted open/closed map
// (localStorage `aos-livetune-sections`). The Actions row is deliberately not
// in here — Copy URL / Reset stay always-visible at the bottom.
// `lightingDirectional` (the sun's own knobs) is split OUT of `shadow` (the
// shared catcher-plane knobs only) into its own section — both still edit the
// same `shadow` store slice (see sectionOfKey below). Adding/removing an id
// here is safe without a SECTIONS_KEY bump: the persisted-state merge
// (`{...base, ...JSON.parse(raw)}` below) just tolerates a missing or stale
// leftover key.
const SECTION_IDS = ['character', 'quality', 'environment', 'backdrop', 'camera', 'shadow', 'lightingDirectional', 'lightingEnvironment', 'lightingSpotlight', 'lightingHemisphere', 'torch', 'pbr', 'skin', 'toon', 'debug']
const SECTIONS_KEY = 'aos-livetune-sections'

// Sub-group fold state INSIDE the CAMERA section. Deliberately a separate
// localStorage key from SECTIONS_KEY — these ids (preset/target/…) are scoped
// one level deeper than the top-level SECTION_IDS map above, so reusing that
// key/map would collide with a same-named top-level section id if one is ever
// added. Default-open: only `cage`.
const CAMERA_SUB_IDS = ['target', 'cage', 'seeThrough', 'follow', 'blend']
const CAMERA_SUBSECTIONS_KEY = 'aos-livetune-camera-subsections'

// Hand-maintained search text per section for the filter box — keep this in
// sync when adding/renaming controls inside a section (the Section component
// matches filter text against this + the section title).
const SECTION_SEARCH_TEXT = {
  character: 'character avatar select force toon shader',
  quality: 'texture quality resolution gpu bandwidth tier upgrade',
  environment: 'environment splat pose position rotation scale floor y',
  backdrop: 'backdrop background sky color ground plane floor size green screen chroma key',
  camera: 'camera fov cage half extents margin offset position field of view',
  shadow: 'shadow catcher plane opacity floor y map resolution',
  lightingDirectional: 'lighting directional light sun intensity temperature bias blur radius samples frustum size elevation azimuth distance shadow',
  lightingEnvironment: 'lighting environment ibl key light ambient color light probe intensity env map',
  lightingSpotlight: 'lighting spotlight color intensity range angle penumbra distance height yaw rotate target shadow blur bias add spotlight remove',
  lightingHemisphere: 'lighting hemisphere sky ground color intensity',
  pbr: 'pbr roughness metalness cloth teeth accessories',
  skin: 'skin material roughness normal scale thickness subsurface',
  toon: 'toon shader diffuse scale rim intensity fresnel power face flatten',
  debug: 'debug mirror ball probe sphere ibl state',
}

const LABEL = { color: 'var(--m-fg-3)', fontSize: 10, marginBottom: 2, letterSpacing: 0.3 }
const INPUT = {
  background: 'var(--m-bg-graph)', color: 'var(--m-fg)', border: '1px solid var(--m-line-hi)',
  borderRadius: 3, fontFamily: 'var(--m-mono)', fontSize: 11,
  padding: '3px 6px', outline: 'none', width: '100%', boxSizing: 'border-box',
}
const BTN = {
  background: 'var(--m-panel-hi)', color: 'var(--m-fg)', border: '1px solid var(--m-line-hi)',
  borderRadius: 3, fontFamily: 'var(--m-font)', fontSize: 11,
  padding: '3px 10px', cursor: 'pointer',
}
const ROW = { display: 'flex', gap: 4, marginBottom: 4 }
const SECTION = { marginTop: 10, paddingTop: 6, borderTop: '1px solid var(--m-line)' }

// Inline dimming for a cluster of controls gated by a boolean toggle (e.g.
// spotlight.enabled): the dependent rows read as inert (dimmed + non-
// interactive) without unmounting them, so toggling the gate back on doesn't
// lose any of their values. The gate checkbox itself stays outside this
// wrapper so it's always fully opaque/interactive.
const gatedStyle = (enabled) => (enabled ? undefined : { opacity: 0.4, pointerEvents: 'none' })

// ──────────────────────────────────────────────────────────────────────
// Small controls
// ──────────────────────────────────────────────────────────────────────

function NumberInput({ value, onChange, step = 0.01, width = 60 }) {
  return (
    <input
      type="number"
      value={Number.isFinite(value) ? value : ''}
      step={step}
      onChange={e => {
        const v = parseFloat(e.target.value)
        if (Number.isFinite(v)) onChange(v)
      }}
      style={{ ...INPUT, width }}
    />
  )
}

function Slider({ value, min, max, step, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        style={{ flex: 1 }}
      />
      <span style={{ color: 'var(--m-fg-2)', fontSize: 10, minWidth: 36, textAlign: 'right' }}>
        {Number.isFinite(value) ? value.toFixed(step < 0.001 ? 4 : step < 0.1 ? 2 : 1) : '—'}
      </span>
    </div>
  )
}

/**
 * LogSlider — constant *ratio* per pixel instead of constant difference.
 *
 * ENG-895: environment scale needs to reach the 100s (Isaac OGS), but a linear
 * 0.01..1000 slider leaves ~0.01 resolution over the low end where most
 * environments actually sit. Log mapping makes 0.5→0.6 and 50→60 the same drag.
 * The numeric field beside it stays authoritative for exact values.
 */
function LogSlider({ value, min, max, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <input
        type="range" min={0} max={1} step={0.001}
        value={toSliderPos(value, min, max)}
        onChange={e => onChange(quantize(fromSliderPos(parseFloat(e.target.value), min, max)))}
        style={{ flex: 1 }}
      />
      <input
        type="number"
        value={Number.isFinite(value) ? value : ''}
        step={0.01}
        min={min}
        onChange={e => {
          const v = parseFloat(e.target.value)
          if (Number.isFinite(v) && v > 0) onChange(v)
        }}
        style={{ ...INPUT, width: 62, flex: 'none' }}
      />
    </div>
  )
}

const INFOTIP_WIDTH = 190

// Small "?" glyph that reveals a floating tooltip on hover — replaces the
// old always-visible paragraph blocks that made the panel feel cluttered.
// Anchored left below the glyph by default; on hover-open we measure the
// glyph against the panel's right edge (data-livetune-panel ancestor) and
// flip to right-anchored if a left anchor would overflow it, so tips near
// the panel's right edge stay fully visible instead of clipping.
function InfoTip({ text }) {
  const [open, setOpen] = useState(false)
  const [side, setSide] = useState('left')
  const ref = useRef(null)

  const handleEnter = () => {
    setOpen(true)
    const el = ref.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const panel = el.closest('[data-livetune-panel]')
    const rightBound = panel
      ? panel.getBoundingClientRect().right - 8 // account for panel padding
      : (typeof window !== 'undefined' ? window.innerWidth - 10 : Infinity)
    setSide(rect.left + INFOTIP_WIDTH > rightBound ? 'right' : 'left')
  }

  return (
    <span
      ref={ref}
      style={{ position: 'relative', display: 'inline-flex', verticalAlign: 'middle' }}
      onMouseEnter={handleEnter}
      onMouseLeave={() => setOpen(false)}
    >
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 12, height: 12, borderRadius: '50%', border: '1px solid var(--m-line-hi)',
        color: 'var(--m-fg-3)', fontSize: 9, lineHeight: 1, cursor: 'help', userSelect: 'none',
      }}>?</span>
      {open && (
        <span style={{
          position: 'absolute', top: '140%', [side]: 0, zIndex: 30,
          width: INFOTIP_WIDTH, background: 'var(--m-panel)', border: '1px solid var(--m-line-hi)', borderRadius: 4,
          padding: '6px 8px', color: 'var(--m-fg-2)', fontSize: 10, lineHeight: 1.4,
          letterSpacing: 'normal', textTransform: 'none', fontWeight: 400,
          boxShadow: '0 2px 10px rgba(0,0,0,0.6)', pointerEvents: 'none',
        }}>
          {text}
        </span>
      )}
    </span>
  )
}

// Row helper: a LABEL-styled line with an inline InfoTip after the text.
function LabelTip({ children, tip, style }) {
  return (
    <div style={{ ...LABEL, display: 'flex', alignItems: 'center', gap: 5, ...style }}>
      <span>{children}</span>
      <InfoTip text={tip} />
    </div>
  )
}

function Checkbox({ checked, onChange, label }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: 'var(--m-fg)', fontSize: 11 }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      {label}
    </label>
  )
}

// Read-only texture-quality readout (ENG-757). State lives in the
// textureQuality module singleton (surfaced on window.__TEXTURE_QUALITY__);
// the subscription only re-renders these labels. The user-facing selector is
// in the AppMenu — this is the debug view of what the detection decided.
function TextureQualityReadout() {
  const [, setTick] = useState(0)
  useEffect(() => subscribeTextureQuality(() => setTick(t => t + 1)), [])
  const q = typeof window !== 'undefined' ? window.__TEXTURE_QUALITY__ : null
  if (!q) return <div style={{ color: 'var(--m-fg-3)', fontSize: 10 }}>not initialised (avatar not loaded yet)</div>
  const mbps = Number.isFinite(q.measuredMbps) ? `${q.measuredMbps.toFixed(1)} Mbps${q.measuredIsHint ? ' (prior)' : ''}` : 'unmeasured'
  const row = (k, v) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--m-fg-2)' }}>
      <span style={{ color: 'var(--m-fg-3)' }}>{k}</span><span>{v}</span>
    </div>
  )
  const overrideCount = Object.keys(q.overrides ?? {}).length
  return (
    <div>
      {row('setting', q.setting)}
      {row('gpu cap', q.deviceCap)}
      {row('bandwidth', mbps)}
      {row('auto tier', q.autoTier)}
      {row('active tier', q.activeTier ?? 'loading…')}
      {q.upgrade?.active && row('upgrading', `${q.upgrade.done}/${q.upgrade.total}`)}
      {overrideCount > 0 && row('pinned textures', overrideCount)}
    </div>
  )
}

// `testId` tags the typeable hex field (the swatch is a native color picker,
// which Playwright can't drive) so a spec can address one color row directly
// instead of walking the DOM from its label.
function ColorInput({ value, onChange, testId }) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <input
        type="color" value={value}
        onChange={e => onChange(e.target.value)}
        style={{ width: 28, height: 22, padding: 0, border: '1px solid var(--m-line-hi)', borderRadius: 3, background: 'none', cursor: 'pointer' }}
      />
      <input
        type="text" value={value}
        data-testid={testId}
        onChange={e => onChange(e.target.value)}
        style={{ ...INPUT, flex: 1 }}
      />
    </div>
  )
}

function XYZ({ value, onChange, step = 0.05 }) {
  const [x, y, z] = value
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      <NumberInput value={x} step={step} onChange={v => onChange([v, y, z])} width="100%" />
      <NumberInput value={y} step={step} onChange={v => onChange([x, v, z])} width="100%" />
      <NumberInput value={z} step={step} onChange={v => onChange([x, y, v])} width="100%" />
    </div>
  )
}

// Collapsible group: an accent-tinted header bar (chevron + uppercase title)
// over a padded, foldable body — mirrors the content browser's MaterialInspector
// collapsible sections so the tune panel reads with the same visual language.
// Fold state lives in LiveTunePanel (persisted) and is passed in as `open` +
// `onToggle` so every section shares one map.
// Lightweight sub-header for grouping controls within one Section body (e.g.
// the SHADOW section's CATCHER PLANE block) — reuses the LABEL look, just
// bolder/uppercase, rather than introducing a new heavyweight component.
const SUBHEADER = { ...LABEL, textTransform: 'uppercase', fontWeight: 600, color: 'var(--m-fg-3)', marginTop: 8, marginBottom: 4 }
const SUBDIVIDER = { borderTop: '1px solid var(--m-line)', marginTop: 10, marginBottom: 2 }

const DEFAULT_ACCENT = 'var(--m-accent)'

// One spotlight slot's full control set — shared by LIGHTING — SPOT's
// slot 1 (always present, no remove) and slots 2/3 (revealed by "+ ADD
// SPOT", each with its own remove). Pulled out so the three slots can't
// drift from each other; `v`/`onSet` are one liveTuneStore spotlight slice and
// its patch setter (`patch => set({ spotlight: patch })` etc).
function SpotlightControls({ v, onSet, checkboxLabel, onRemove }) {
  return (
    <>
      {onRemove ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={LABEL}>
            <Checkbox checked={v.enabled} onChange={val => onSet({ enabled: val })} label={checkboxLabel} />
          </div>
          <button type="button" onClick={onRemove} style={{ ...BTN, padding: '1px 7px', fontSize: 10, marginLeft: 'auto' }}
            title="Remove this spotlight">
            ✕ remove
          </button>
        </div>
      ) : (
        <div style={LABEL}>
          <Checkbox checked={v.enabled} onChange={val => onSet({ enabled: val })} label={checkboxLabel} />
        </div>
      )}
      <div style={{ ...LABEL, marginTop: 4 }}>
        <Checkbox checked={v.gizmo} onChange={val => onSet({ gizmo: val })} label="SHOW GIZMO (cone)" />
      </div>
      <div style={gatedStyle(v.enabled)}>
        <div style={{ ...LABEL, marginTop: 4 }}>COLOR</div>
        <ColorInput value={v.color} onChange={val => onSet({ color: val })} />
        <div style={{ ...LABEL, marginTop: 4 }}>INTENSITY</div>
        <Slider value={v.intensity} min={0} max={300} step={1} onChange={val => onSet({ intensity: val })} />
        <div style={{ ...LABEL, marginTop: 4 }}>RANGE (m, 0 = infinite)</div>
        <Slider value={v.range} min={0} max={50} step={0.5} onChange={val => onSet({ range: val })} />
        <div style={{ ...LABEL, marginTop: 4 }}>ANGLE (°)</div>
        <Slider value={v.angle} min={5} max={85} step={1} onChange={val => onSet({ angle: val })} />
        <div style={{ ...LABEL, marginTop: 4 }}>PENUMBRA</div>
        <Slider value={v.penumbra} min={0} max={1} step={0.02} onChange={val => onSet({ penumbra: val })} />
        <div style={{ ...LABEL, marginTop: 4 }}>DISTANCE (local +Z, m)</div>
        <Slider value={v.distance} min={0} max={15} step={0.1} onChange={val => onSet({ distance: Math.max(0, val) })} />
        <div style={{ ...LABEL, marginTop: 4 }}>HEIGHT (local +Y, m)</div>
        <Slider value={v.height} min={-5} max={10} step={0.1} onChange={val => onSet({ height: val })} />
        <div style={{ ...LABEL, marginTop: 4 }}>ROTATE / YAW (°)</div>
        <Slider value={v.yaw} min={-180} max={180} step={1} onChange={val => onSet({ yaw: val })} />
        <LabelTip style={{ marginTop: 4 }}
          tip="The spotlight always points at a target on the central Y axis. This slider raises/lowers that target, tilting the beam up or down.">
          TARGET HEIGHT (world +Y, m)
        </LabelTip>
        <Slider value={v.targetHeight} min={-2} max={5} step={0.05} onChange={val => onSet({ targetHeight: val })} />
        <div style={{ ...LABEL, marginTop: 8 }}>SHADOW BLUR RADIUS</div>
        <Slider value={v.shadowRadius} min={0} max={40} step={1} onChange={val => onSet({ shadowRadius: val })} />
        <div style={{ ...LABEL, marginTop: 4 }}>SHADOW BLUR SAMPLES</div>
        <Slider value={v.shadowBlurSamples} min={1} max={100} step={1} onChange={val => onSet({ shadowBlurSamples: val })} />
        <div style={{ ...LABEL, marginTop: 4 }}>SHADOW MAP RESOLUTION</div>
        <select
          value={v.shadowMapSize}
          onChange={e => onSet({ shadowMapSize: parseInt(e.target.value, 10) })}
          style={INPUT}
        >
          <option value={1024}>1024 — low</option>
          <option value={2048}>2048 — medium</option>
          <option value={4096}>4096 — high</option>
        </select>
        <div style={{ ...LABEL, marginTop: 4 }}>SPOT SHADOW BIAS</div>
        <Slider value={v.shadowBias} min={-0.005} max={0.001} step={0.0001} onChange={val => onSet({ shadowBias: val })} />
      </div>
    </>
  )
}

function Section({ id, title, open, filter = '', onToggle, modified = false, children, accent = DEFAULT_ACCENT, resetKey }) {
  // `filter` is a pure view aid: a non-empty query force-opens matching
  // sections and dims non-matching ones WITHOUT mutating the persisted `open`
  // map, so clearing the filter restores the exact persisted fold state. Match
  // text is this section's curated keywords (SECTION_SEARCH_TEXT) plus its
  // title. Computed here (not at each call site) so a section spells its id +
  // title once rather than four times.
  const q = filter.trim()
  const matches = !q || matchesQuery(filter, [SECTION_SEARCH_TEXT[id], title])
  const isOpen = open || (!!q && matches)
  const dimmed = !!q && !matches
  const [justReset, setJustReset] = useState(false)
  const handleReset = (e) => {
    e.stopPropagation()
    liveTuneStore.resetSlice(resetKey)
    setJustReset(true)
    setTimeout(() => setJustReset(false), 1200)
  }
  return (
    <div style={{ marginTop: 6, border: '1px solid var(--m-line-hi)', borderRadius: 4, overflow: 'hidden', opacity: dimmed ? 0.35 : 1 }}>
      <div
        data-section={id}
        role="button"
        tabIndex={0}
        onClick={() => onToggle(id)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            if (e.key === ' ') e.preventDefault()
            onToggle(id)
          }
        }}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: `color-mix(in srgb, ${accent} 12%, transparent)`, padding: '5px 8px',
          cursor: 'pointer', userSelect: 'none',
        }}
      >
        <span style={{ color: accent, fontSize: 20, lineHeight: 1 }}>{isOpen ? '▾' : '▸'}</span>
        <span style={{
          color: accent, fontSize: 10, fontWeight: 600,
          letterSpacing: '0.12em', textTransform: 'uppercase', flex: 1,
        }}>{title}</span>
        {/* Un-shared edits flag their section even while it's folded, so a
           stray change can't hide in a collapsed group. The title above owns
           `flex: 1`, so this and the reset action below just sit in the
           trailing run — no marginLeft:'auto' needed. */}
        {modified && (
          <span style={{ color: 'var(--m-warn)', fontSize: 9, letterSpacing: 0.3 }}>
            ● modified
          </span>
        )}
        {resetKey && (
          justReset ? (
            <span style={{ color: 'var(--m-online)', fontSize: 9 }}>reset ✓</span>
          ) : (
            <button
              type="button"
              data-section-reset={id}
              onClick={handleReset}
              title={`Reset ${title} to defaults`}
              style={{
                background: 'none', border: 'none', color: accent, cursor: 'pointer',
                fontSize: 13, lineHeight: 1, padding: '0 2px', opacity: 0.8,
              }}
            >↺</button>
          )
        )}
      </div>
      {isOpen && <div style={{ padding: '8px 8px 10px' }}>{children}</div>}
    </div>
  )
}

// Nested, indented group used INSIDE the CAMERA section body to break the
// camera model's large surface into foldable pieces without introducing a
// second heavyweight Section-style header. `lockKey`, when
// given, shows a 🔒 badge + "release" action when that camera sub-path has
// been touched this session (see liveTuneStore's `_touched` / setCameraFromPanel
// — a touched path is a dev override a blueprint node won't clobber).
function SubGroup({ id, label, open, onToggle, lockKey, children }) {
  const touched = lockKey && liveTuneStore.isTouched(lockKey)
  return (
    <div data-camera-subgroup={id} style={{ marginTop: 6, borderLeft: `2px solid ${CATEGORY_COLORS.Camera}`, paddingLeft: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button type="button" data-camera-subgroup-toggle={id} onClick={() => onToggle(id)} aria-expanded={open}
          style={{ background: 'none', border: 'none', color: CATEGORY_COLORS.Camera, cursor: 'pointer', fontSize: 13, padding: 0 }}>
          {open ? '▾' : '▸'}
        </button>
        <span style={{ ...LABEL, marginBottom: 0, flex: 1, color: 'var(--m-fg-2)' }}>{label}</span>
        {touched && (
          <span style={{ fontSize: 9, color: 'var(--m-warn)' }}>🔒 <button type="button"
            onClick={() => { liveTuneStore.releaseTouched(lockKey) }}
            style={{ background: 'none', border: 'none', color: 'var(--m-accent)', cursor: 'pointer', fontSize: 9, textDecoration: 'underline', padding: 0 }}>release</button></span>
        )}
      </div>
      {open && <div style={{ marginTop: 4 }}>{children}</div>}
    </div>
  )
}

// Live status readout for the CAMERA section — poll-driven (setInterval),
// NOT a useFrame subscription (CLAUDE.md: never call React state setters
// from useFrame; the camera controller lives entirely outside React so this
// is a plain DOM timer, same pattern as TextureQualityReadout / the envProbe
// poll above). Cleared on unmount. Combines the live transform (from
// window.__CAMERA_CONTROLLER__.getTransform()) and the see-through debug
// global with the reactive `camera` store slice for the parts that don't need
// a live read (preset, fov target, follow/see-through enabled flags).
//
// NOTE (deferred): these chips only render in the section BODY, so they're
// glanceable while the CAMERA section is open but invisible while it's
// collapsed. Surfacing a glance-while-collapsed summary in the Section header
// itself would require threading a header-extra slot through the generic
// <Section> component — left for a follow-up.
function CameraStatusChips({ camera }) {
  const [live, setLive] = useState(null)
  useEffect(() => {
    const id = setInterval(() => {
      const t = typeof window !== 'undefined' ? window.__CAMERA_CONTROLLER__?.getTransform?.() : null
      setLive(t ?? null)
    }, 100)
    return () => clearInterval(id)
  }, [])

  const chip = (label, active) => (
    <span style={{
      fontSize: 9, padding: '1px 6px', borderRadius: 8, lineHeight: '14px',
      border: `1px solid ${active ? CATEGORY_COLORS.Camera : 'var(--m-line-hi)'}`,
      color: active ? CATEGORY_COLORS.Camera : 'var(--m-fg-3)',
    }}>{label}</span>
  )

  const followOn = !!camera.follow?.enabled
  const seeThroughOn = !!(camera.seeThrough?.enabled || camera.seeThrough?.auto)

  return (
    <div style={{ marginTop: 6, marginBottom: 2 }}>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {chip(`${Math.round(camera.fov)}°`, true)}
        {chip(followOn ? '● follow' : '○ follow', followOn)}
        {chip(seeThroughOn ? '● see-thru' : '○ see-thru', seeThroughOn)}
      </div>
      <div style={{ fontSize: 9, color: 'var(--m-fg-3)', marginTop: 3 }}>
        target [{live ? live.target.map(v => v.toFixed(2)).join(', ') : '—, —, —'}] · live fov {live ? live.fov.toFixed(0) : '—'}°
      </div>
    </div>
  )
}

// Small greyed footnote for controls that are deliberately per-browser.
function LocalNote({ children }) {
  return <div style={{ fontSize: 9, color: 'var(--m-fg-3)', marginTop: 6 }}>{children}</div>
}

// ──────────────────────────────────────────────────────────────────────
// Panel
// ──────────────────────────────────────────────────────────────────────

export default function LiveTunePanel({ visible: visibleProp, onBeforeReload, topOffset = 0 }) {
  const visibleFromEnv = useMemo(() => {
    if (typeof window === 'undefined') return false
    return new URLSearchParams(window.location.search).get('tune') === '1'
      || localStorage.getItem('tune') === '1'
  }, [])
  const visible = visibleProp ?? visibleFromEnv

  const tune = useLiveTune()
  // Start folded — the panel opens as a compact "LIVE TUNE ▸" chip the user
  // expands when needed.
  const [collapsed, setCollapsed] = useState(true)
  // environments: null = loading; [] = loaded-but-empty; Array = list.
  // envError holds the server-error message so the user sees the failure
  // (per CLAUDE.md: no silent fallbacks to bundled data).
  const [environments, setEnvironments] = useState(null)
  const [envError, setEnvError] = useState(null)
  const [envProbe, setEnvProbe] = useState(null)
  const [characters, setCharacters] = useState(null)
  // Persist-pose UI state. MUST live above the `if (!visible) return null`
  // guard below — a hook called after an early return changes the hook
  // count when `visible` flips false→true (entering live mode), which
  // crashes React with "Rendered more hooks than during the previous
  // render." Keep all hooks unconditional, before any early return.
  const [savePose, setSavePose] = useState({ status: 'idle', msg: null })
  // Shared-tune save state (same hook-ordering constraint as savePose).
  const [saveTune, setSaveTune] = useState({ status: 'idle', msg: null })
  // Footer change-list (diff) expanded/collapsed.
  const [diffOpen, setDiffOpen] = useState(false)
  // "Copied ✓" confirmation flash on the Copy URL button — purely a UI
  // affordance, not a tunable, so it lives in local component state rather
  // than the store. MUST stay above the `if (!visible)` guard (see savePose
  // note above).
  const [copied, setCopied] = useState(false)

  // Per-section fold state for the collapsible groups below. UI-only (not a
  // tunable), persisted to its own localStorage key so a reload keeps the
  // layout. Defaults all-open. MUST stay above the `if (!visible)` guard so the
  // hook count is stable when the panel toggles visible (see savePose note).
  // Curated defaults: only the sections a dev needs open on a first visit
  // start expanded — the rest (quality, spotlight, hemisphere, pbr, skin,
  // toon, debug) start closed to cut down on initial scroll/clutter. Any
  // locally persisted fold state (localStorage `aos-livetune-sections`)
  // still wins over this for a returning user.
  const OPEN_BY_DEFAULT = ['character', 'environment', 'camera', 'shadow', 'lightingDirectional', 'lightingEnvironment']
  const [openSections, setOpenSections] = useState(() => {
    const base = Object.fromEntries(SECTION_IDS.map(id => [id, OPEN_BY_DEFAULT.includes(id)]))
    if (typeof window === 'undefined') return base
    try {
      const raw = localStorage.getItem(SECTIONS_KEY)
      if (raw) return { ...base, ...JSON.parse(raw) }
    } catch { /* ignore corrupt persisted state */ }
    return base
  })
  const toggleSection = (id) => setOpenSections(prev => {
    const next = { ...prev, [id]: !prev[id] }
    try { localStorage.setItem(SECTIONS_KEY, JSON.stringify(next)) } catch { /* quota etc. */ }
    return next
  })
  const setAllSections = (value) => setOpenSections(() => {
    const next = Object.fromEntries(SECTION_IDS.map(id => [id, value]))
    try { localStorage.setItem(SECTIONS_KEY, JSON.stringify(next)) } catch { /* quota etc. */ }
    return next
  })

  // Sub-group fold state INSIDE the CAMERA section — separate localStorage
  // key (CAMERA_SUBSECTIONS_KEY), see the constant's comment.
  // MUST stay above the `if (!visible)` guard for the same hooks-order reason
  // as openSections above.
  const [openCameraSub, setOpenCameraSub] = useState(() => {
    const base = Object.fromEntries(CAMERA_SUB_IDS.map(id => [id, id === 'cage']))
    if (typeof window === 'undefined') return base
    try {
      const raw = localStorage.getItem(CAMERA_SUBSECTIONS_KEY)
      if (raw) return { ...base, ...JSON.parse(raw) }
    } catch { /* ignore corrupt persisted state */ }
    return base
  })
  const toggleCameraSub = (id) => setOpenCameraSub(prev => {
    const next = { ...prev, [id]: !prev[id] }
    try { localStorage.setItem(CAMERA_SUBSECTIONS_KEY, JSON.stringify(next)) } catch { /* quota etc. */ }
    return next
  })

  // Dev-only "show the camera cage gizmo in the viewport" flag — ephemeral,
  // lives in liveTuneStore outside `state` (see useCameraCageGizmoVisible).
  const cageGizmoVisible = useCameraCageGizmoVisible()

  // Which of the extra LIGHTING — SPOT slots (2/3, max 3 total) are
  // revealed in the panel. UI-only, session-local (not persisted) — a slot's
  // own `enabled` flag is the durable state; this only controls whether its
  // block is shown at all, so "+ ADD SPOT" only needs to be clicked once
  // per session even if the user later unchecks that slot's own toggle.
  // Initialized from the store so a slot someone left enabled (e.g. via Copy
  // URL / a shared tune) shows its block on load rather than hiding behind
  // the + wall again.
  const [revealedSpotSlots, setRevealedSpotSlots] = useState(() => ({
    2: !!tune.spotlight2?.enabled,
    3: !!tune.spotlight3?.enabled,
  }))

  // Filter box: UI-only, NOT persisted. Passed straight into each <Section>,
  // which derives its own effective-open/dimmed from it without ever mutating
  // `openSections`, so clearing the input restores exactly the persisted fold
  // state (see the Section component).
  const [filterQuery, setFilterQuery] = useState('')

  useEffect(() => {
    if (!visible) return
    loadCharacters().then(list => setCharacters(list)).catch(() => setCharacters(null))
  }, [visible])

  // ENG-529: env list is per-character, sourced from /api/characters/{slug}/environments.
  // Refetches when the selected character changes; surfaces server errors
  // loudly via envError instead of silently falling back to an old manifest.
  // Also re-derives from CustomAssetManager whenever new envs are uploaded or
  // deleted from the content browser — without this, the dropdown stays stale
  // until the user reloads.
  const activeCharacterSlug = getCurrentCharacterId(characters ?? undefined)
  // Rows filed in a reserved dot-folder (the interop suite's '/.e2e' corral)
  // are hidden here too — the env dropdown was the other place a leaked e2e
  // environment was user-visible. hiddenFolders.js has the escape hatch.
  const envRowVisible = r => showHiddenFolders() || !isHiddenFolderPath(r.folderPath ?? r.folder_path)
  const buildEnvList = () => (CustomAssetManager.listEnvironmentAssets() ?? []).filter(envRowVisible).map(r => ({
    id: r.id,
    url: r.fileUrl ?? r.file_url,
    label: r.name ?? r.id,
    defaultPose: r.defaultPose ?? r.default_pose ?? {},
    defaultTune: r.defaultTune ?? r.default_tune ?? {},
    version: r.updatedAt ?? r.updated_at ?? null,
  }))
  useEffect(() => {
    if (!visible) return
    if (!activeCharacterSlug) return
    let cancelled = false
    setEnvironments(null)
    setEnvError(null)
    listEnvironments(activeCharacterSlug)
      .then(rows => {
        if (cancelled) return
        // DTO ships file_url + fileUrl; LiveTunePanel uses .url so we normalize here.
        const list = (rows ?? []).filter(envRowVisible).map(r => ({
          id: r.id,
          url: r.fileUrl ?? r.file_url,
          label: r.name ?? r.id,
          defaultPose: r.defaultPose ?? r.default_pose ?? {},
          defaultTune: r.defaultTune ?? r.default_tune ?? {},
          version: r.updatedAt ?? r.updated_at ?? null,
        }))
        setEnvironments(list)
      })
      .catch(err => {
        if (cancelled) return
        console.warn('[LiveTunePanel] listEnvironments failed:', err)
        setEnvironments([])
        setEnvError(String(err?.message ?? err))
      })
    // Subscribe to CustomAssetManager so uploads/deletes from the content
    // browser refresh the dropdown immediately. CAM's notify() fires on
    // every list mutation; we re-derive from its in-memory env list rather
    // than re-hitting the API.
    const unsub = CustomAssetManager.subscribe(() => {
      if (cancelled) return
      setEnvironments(buildEnvList())
    })
    return () => { cancelled = true; unsub?.() }
  }, [visible, activeCharacterSlug])

  // Poll __ENV_PROBE__ for diagnostic display.
  useEffect(() => {
    if (!visible) return
    const id = setInterval(() => {
      const p = typeof window !== 'undefined' ? window.__ENV_PROBE__ : null
      setEnvProbe(p ? { ...p } : null)
    }, 500)
    return () => clearInterval(id)
  }, [visible])

  if (!visible) return null

  const set = (patch) => liveTuneStore.setState(patch)

  // Environment TRANSFORM edits (pose / rotation / scale) share an undo stack
  // with the viewport gizmo, so Ctrl+Z behaves the same whether you dragged a
  // handle or typed in a field (ENG-895). `key` identifies the field so
  // consecutive edits to the SAME one coalesce — typing "1.61" or dragging the
  // scale slider is one undo step, not one per event.
  //
  // Deliberately NOT applied to floorY: the shared stack stores
  // position/rotation/scale (what the gizmo can express), so an entry recorded
  // for floorY would restore the transform without restoring floorY — an undo
  // that silently under-delivers. Nor to the environment dropdown, which
  // replaces the pose wholesale for a DIFFERENT object (see the clear() below).
  const setPose = (key, posePatch) => {
    const a = getAdapter('environment')
    if (a?.isAvailable()) transformHistory.recordEdit('environment', a.read(), key)
    set({ environment: { pose: posePatch } })
  }
  // Camera edits go through setCameraFromPanel so the touched path becomes a dev
  // override that a blueprint camera node won't clobber this session (precedence:
  // URL > panel > blueprint > default). Patch shape is the `camera` sub-object.
  const setCam = (cameraPatch) => liveTuneStore.setCameraFromPanel(cameraPatch)
  // Bring the torch to where you're looking FROM, and hand it to the gizmo.
  //
  // Its spherical placement orbits [0, targetHeight, 0] at DISTANCE metres,
  // which lands behind the camera whenever the view is framed close on the
  // subject — exactly the framing a specular probe is judged at. Light, emitter
  // cube and gizmo are then all off screen with nothing to grab, which reads as
  // "the torch can't be moved" rather than "the torch is behind you".
  //
  // Writes through the adapter, so the jump lands in the same spherical knobs a
  // drag does, and selects the row so the gesture ends with a handle on screen.
  const placeTorchAtCamera = () => {
    const oc = typeof window !== 'undefined' ? window.__ORBIT_CONTROLS__ : null
    const cam = oc?.object
    if (!cam || !oc.target) return
    const a = getAdapter('torch')
    // The headlamp owns the position outright, so "drop it here" means letting
    // go of the camera first — the adapter refuses the write otherwise.
    set({ torch: { followCamera: false } })
    transformHistory.recordEdit('torch', a.read(), 'torch-place')
    a.write({
      position: placeAtView(
        [cam.position.x, cam.position.y, cam.position.z],
        [oc.target.x, oc.target.y, oc.target.z],
      ),
    })
    sceneSelection.select('torch')
  }
  const { environment, camera, lighting, shadow, hemisphere, backdrop, debug, toon, pbr, pbrSkin, spotlight, spotlight2, spotlight3, torch } = tune

  // Persist the live-tuned pose back to the asset's default_pose so it loads
  // correctly for everyone, instead of forcing the artist to re-type values in
  // the inspector (which carries a scale-0 trap). Server-is-source-of-truth:
  // this PATCHes the row; the dropdown re-reads it on the next select.
  // (savePose state is declared above the early-return guard — see note there.)
  //
  // Which row is actually loaded: assetId first (so a format-pinned load is
  // recognized), URL as the fallback, and NOTHING while no splat is loaded —
  // a stale id would otherwise re-enable the Save-for-everyone buttons against
  // a shared org row. The whole rule lives in resolveCurrentEnvironment so it
  // is unit-testable rather than buried in JSX; see it for the full rationale.
  const currentEnv = resolveCurrentEnvironment(environments, environment)
  const saveCurrentPoseAsDefault = async () => {
    if (!currentEnv?.id) {
      setSavePose({ status: 'error', msg: 'no environment selected' })
      return
    }
    setSavePose({ status: 'saving', msg: null })
    try {
      const pose = normalizePose(environment.pose)
      // Route through CAM so the in-memory list updates and a stale id triggers
      // a re-sync (see updateEnvironmentAsset) rather than a dead-end error.
      // camelCase `defaultPose` to match the asset-manager's Update contract
      // (a snake_case key is silently dropped server-side).
      await CustomAssetManager.updateEnvironmentAsset(currentEnv.id, { defaultPose: pose })
      setSavePose({ status: 'saved', msg: 'saved as default' })
    } catch (err) {
      setSavePose({
        status: 'error',
        msg: isRecordGoneError(err) ? STALE_ASSET_MESSAGE : String(err?.message ?? err),
      })
    }
  }

  // Shared-tune save: persist the current lighting look (envTune vocabulary)
  // to this environment's default_tune so every viewer gets it on env apply —
  // the server-side answer to per-browser localStorage drift. camelCase key
  // (server dual-accepts, but match the defaultPose precedent).
  const saveCurrentTuneForEveryone = async () => {
    if (!currentEnv?.id) {
      setSaveTune({ status: 'error', msg: 'no environment selected' })
      return
    }
    setSaveTune({ status: 'saving', msg: null })
    try {
      const tuneSnapshot = collectTune(liveTuneStore.getState())
      await CustomAssetManager.updateEnvironmentAsset(currentEnv.id, { defaultTune: tuneSnapshot })
      setTuneBaseline(tuneSnapshot)
      setSaveTune({ status: 'saved', msg: 'lighting saved for everyone' })
    } catch (err) {
      setSaveTune({
        status: 'error',
        msg: isRecordGoneError(err) ? STALE_ASSET_MESSAGE : String(err?.message ?? err),
      })
    }
  }
  // Per-knob diff vs the shared look (server baseline; DEFAULTS when the env
  // was never saved — that IS what a fresh teammate sees). Recomputed every
  // render — the panel re-renders on each store change via useLiveTune.
  const tuneDiff = currentEnv ? diffTune(tune) : []
  const tuneNeverSaved = getTuneBaseline() == null
  const tuneIsDirty = tuneDiff.length > 0

  // The footer's Save button is always mounted, so it has to say WHY it is
  // disabled — an unexplained dead button is the same dead end as a missing
  // one. Order matters: "no environment" is the blocking fact, and reporting
  // "nothing changed" first would send someone hunting for a knob to turn on
  // a panel that has nowhere to save to.
  const saveDisabledReason = !currentEnv
    ? 'Load an environment to publish a shared look — it saves onto that asset.'
    : !tuneIsDirty
      ? 'Nothing to publish — this already matches the shared look.'
      : null
  const saveEnabled = saveDisabledReason == null && saveTune.status !== 'saving'

  // Header chip: the one always-visible answer to "am I seeing what everyone
  // sees?". Green in sync / amber N-not-shared / violet publishing / red error.
  const chipState = saveTune.status === 'saving' ? 'saving'
    : saveTune.status === 'error' ? 'error'
    : tuneIsDirty ? 'dirty' : 'clean'
  const CHIP_COLORS = { clean: 'var(--m-online)', dirty: 'var(--m-warn)', saving: 'var(--m-accent)', error: 'var(--m-danger)' }
  const chipColor = CHIP_COLORS[chipState]
  const chipText = chipState === 'saving' ? 'publishing…'
    : chipState === 'error' ? 'save failed'
    : tuneIsDirty ? `${tuneDiff.length} not shared`
    : saveTune.status === 'saved' ? 'saved ✓' : 'in sync'

  // Which UI section owns a changed knob — drives the "● modified" flags.
  // The `shadow` store slice is split across TWO sections: the catcher-plane
  // fields (opacity/floorYOverride/mapSize — "that's a global thing") stay in
  // SHADOW, everything else about the sun lives in LIGHTING — DIRECTIONAL
  // LIGHT. Both still resetSlice('shadow') (see resetKey below), since it's
  // one store slice either way. A key that maps to a section id nothing
  // renders would silently drop its badge, so this switch must only ever
  // name a live <Section id>.
  const SHADOW_CATCHER_FIELDS = new Set(['opacity', 'floorYOverride', 'mapSize'])
  const sectionOfKey = (key) => {
    const [slice, field] = key.split('.')
    switch (slice) {
      case 'lighting': return 'lightingEnvironment'
      case 'spotlight': case 'spotlight2': case 'spotlight3': return 'lightingSpotlight'
      case 'hemisphere': return 'lightingHemisphere'
      case 'torch': return 'torch'
      case 'backdrop': return 'backdrop'
      case 'pbr': return 'pbr'
      case 'pbrSkin': return 'skin'
      case 'camera': return 'camera'
      case 'shadow': return SHADOW_CATCHER_FIELDS.has(field) ? 'shadow' : 'lightingDirectional'
      default: return null
    }
  }
  const modifiedSections = new Set(tuneDiff.map(d => sectionOfKey(d.key)).filter(Boolean))

  // Compact value formatting for the diff rows.
  const fmtTuneValue = (v) => {
    if (typeof v === 'boolean') return v ? 'on' : 'off'
    if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toFixed(2)
    if (v == null) return '—'
    return String(v)
  }

  // Show the TOON section only when the active character actually uses
  // MeshToonMaterial — the uniforms are a no-op on PBR characters and
  // the knobs would just mislead. The forceToon override flips this on
  // for any character.
  const currentCharacterId = getCurrentCharacterId(characters ?? undefined)
  const isToonChar = (characters ?? []).find(c => c.id === currentCharacterId)?.material?.kind === 'toon'

  const copyShareUrl = () => {
    const params = new URLSearchParams()
    // Full-diff carrier: everything the panel can change (fov, all
    // spotlight.*/hemisphere.*/lighting.*, shadow.*, environment pose, etc.)
    // round-trips via `tp`, instead of the old ~8 hand-picked fields.
    // parseTuneUrlParams() still understands the legacy per-field params
    // (splat/sx/.../cage/noibl/shadow/shadowY/mirror/probe) for old bookmarks.
    const diff = computeDiff(DEFAULTS, tune)
    if (Object.keys(diff).length) params.set('tp', JSON.stringify(diff))
    params.set('live', '1')
    const url = `${window.location.origin}${window.location.pathname}?${params.toString()}`
    navigator.clipboard?.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
    return url
  }

  return (
    <div data-livetune-panel style={{
      position: 'absolute',
      // topOffset: when the blueprint editor is COLLAPSED it portals a
      // fixed top-right menu bar onto document.body (z 50), and the viewport
      // goes full width — putting that bar exactly on top of this chip at
      // top:10. The chip was still mounted but unclickable, which read as
      // "the tune panel disappeared". App passes the clearance in that state.
      top: 10 + topOffset, right: 10,
      background: 'color-mix(in srgb, var(--m-panel) 88%, transparent)', border: '1px solid var(--m-line-hi)', borderRadius: 6,
      fontFamily: 'var(--m-font)', fontSize: 11,
      // z 25 > OutlinerRail's 20. Both are ?tune=1 dev chrome and both used to
      // be 20, so the winner was decided by DOM order — App renders the rail
      // AFTER this panel, so the rail painted on top. That only bites in a split
      // layout: ?live=1 does NOT collapse the blueprint editor (the split is
      // owned by editorCollapsed), so the viewport pane is ~40% of the window —
      // about 500px, while the docked rail (244) plus this panel (300 + 10) need
      // ~554. The overlap lands on this panel's left column, which is exactly
      // where the CAMERA sub-group chevrons are indented, so they became
      // unclickable (and hung Playwright's click until the test timed out).
      // A floating tool window outranks docked chrome; the rail's covered strip
      // is its right edge, whose rows stay clickable further left.
      color: 'var(--m-fg)', zIndex: 25, width: collapsed ? 'auto' : PANEL_WIDTH,
      // NOT `vh`. The app root carries a CSS zoom for the UI-scale setting
      // (uiScale.js, default step 'medium' = 1.1), and `vh` is a slice of the
      // viewport in UNZOOMED pixels — so `calc(100vh - 20px)` computed 700 and
      // the zoom then painted the box 770 tall. Measured in a 1280x720 window:
      // the panel ran from y=62 to y=832 and the pinned footer laid out at
      // y=790..831, entirely below the window with no scrollbar to reach it.
      // That footer is the ONLY commit surface — Copy URL, Reset all, and
      // "Save for everyone" — so the shared-tune feature read as missing to
      // two people independently, on a panel that was rendering it the whole
      // time. `%` resolves against the containing block (the viewport pane) in
      // this element's OWN zoomed units and lands on the window, and tracks
      // topOffset for free. Same bug, same fix as materialGraph.css `.sg-root`.
      maxHeight: `calc(100% - ${20 + topOffset}px)`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      {/* Pinned header — the sync chip is visible even while collapsed and
         never scrolls away, so "am I seeing what everyone sees?" is always
         one glance away. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px',
        flexShrink: 0, borderBottom: collapsed ? 'none' : '1px solid var(--m-line-hi)',
      }}>
        <button data-testid="livetune-toggle" onClick={() => setCollapsed(!collapsed)} style={{ ...BTN, padding: '1px 6px', fontSize: 16, lineHeight: 1 }}>
          {collapsed ? '▸' : '▾'}
        </button>
        <div style={{ color: 'var(--m-accent)', letterSpacing: 0.5 }}>LIVE TUNE</div>
        {currentEnv && (
          <span data-testid="tune-sync-chip" data-state={chipState} title={
            chipState === 'dirty'
              ? 'Your lighting differs from the shared look — see the bar at the bottom of the panel'
              : chipState === 'error' ? (saveTune.msg ?? 'Save failed')
              : 'Shared look for this environment'
          } style={{
            marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5,
            fontSize: 9, letterSpacing: 0.4, padding: '2px 8px', borderRadius: 99,
            whiteSpace: 'nowrap', color: chipColor,
            border: `1px solid color-mix(in srgb, ${chipColor} 33%, transparent)`, background: `color-mix(in srgb, ${chipColor} 8%, transparent)`,
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: chipColor, flexShrink: 0 }} />
            {chipText}
          </span>
        )}
      </div>

      {!collapsed && <div style={{ overflowY: 'auto', flex: 1, padding: '0 10px 10px' }}>

        {/* ── Filter + expand/collapse-all ───────────────────────────── */}
        {/* Purely a view aid over the persisted fold state — each Section
            derives its own open/dimmed from `filter`. Typing here never
            writes to aos-livetune-sections. */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
          <input
            type="text"
            value={filterQuery}
            onChange={e => setFilterQuery(e.target.value)}
            placeholder="filter sections…"
            style={{ ...INPUT, flex: 1 }}
          />
          <button type="button" onClick={() => setAllSections(true)} style={{ ...BTN, padding: '3px 6px', fontSize: 10 }}>expand all</button>
          <button type="button" onClick={() => setAllSections(false)} style={{ ...BTN, padding: '3px 6px', fontSize: 10 }}>collapse all</button>
        </div>

        {/* ── Character ──────────────────────────────────────────────── */}
        {/* Selecting a character or toggling forceToon writes through
            characterState / liveTuneStore and reloads — AvatarModel resolves
            the active character once at mount, so a live swap would require
            tearing down and rebuilding the rig + mixer + face player. */}
        <Section id="character" title="CHARACTER" open={openSections.character} filter={filterQuery} onToggle={toggleSection} accent={CATEGORY_COLORS.Character} resetKey="character">
        {characters === null ? (
          <div style={{ color: 'var(--m-fg-3)', fontSize: 10 }}>loading characters…</div>
        ) : (
          <select
            value={currentCharacterId}
            onChange={e => {
              setCurrentCharacterId(e.target.value)
              onBeforeReload?.()
              window.location.reload()
            }}
            style={INPUT}
          >
            {characters.map(c => (
              <option key={c.id} value={c.id}>{c.label || c.id}</option>
            ))}
          </select>
        )}
        <div style={{ marginTop: 4 }}>
          <Checkbox
            checked={!!tune.character?.forceToon}
            onChange={v => {
              set({ character: { forceToon: v } })
              onBeforeReload?.()
              window.location.reload()
            }}
            label="FORCE TOON SHADER"
          />
        </div>
        <LocalNote>local to this browser — never shared</LocalNote>
        </Section>

        {/* ── Texture quality (read-only; selector lives in the AppMenu) ─ */}
        <Section id="quality" title="TEXTURE QUALITY" open={openSections.quality} filter={filterQuery} onToggle={toggleSection} accent={CATEGORY_COLORS.Scene}>
          <TextureQualityReadout />
        </Section>

        {/* ── Environment ────────────────────────────────────────────── */}
        <Section id="environment" title="ENVIRONMENT" open={openSections.environment} filter={filterQuery} onToggle={toggleSection} accent={CATEGORY_COLORS.Scene} resetKey="environment">
        {/* ENG-529: env list comes from /api/characters/{slug}/environments.
            Drop a .ply/.splat/.spz/.ksplat onto the content browser to
            upload one. Per-character — switching the character above
            reloads this list. After uploading, run `npm run bake-ibl`
            against the new URL so EnvProbe has a cubemap cache for it. */}
        {envError ? (
          <div style={{ color: 'var(--m-danger)', fontSize: 10 }}>
            failed to load environments — {envError}
          </div>
        ) : environments === null ? (
          <div style={{ color: 'var(--m-fg-3)', fontSize: 10 }}>loading environments…</div>
        ) : environments.length === 0 ? (
          <div style={{ color: 'var(--m-danger)', fontSize: 10 }}>
            no environments — drop a .ply/.splat onto the content browser to upload one
          </div>
        ) : (
          <select
            // currentEnv matches by assetId first (see its definition above) —
            // load a format-pinned variant (a blueprint node's PLY/SPZ pin,
            // ENG-1070) and the dropdown still shows the actual row instead of
            // falling through to "(custom)" just because splatUrl isn't any
            // option's literal url.
            // Falls back to '' (— none —), NEVER to environments[0].url. That
            // fallback was a display lie with a real cost: with nothing loaded
            // the dropdown showed the first environment as though it were
            // active, so picking that very environment fired no onChange and
            // appeared to do nothing — you had to select a different one and
            // come back (Isaac, 2026-09-01). The control now always says what is
            // actually loaded.
            value={currentEnv
              ? currentEnv.url
              : (environment.splatUrl || '')}
            onChange={e => {
              const url = e.target.value
              // '' = — none —: unload rather than pretending some row is loaded.
              if (!url) {
                set({ environment: { splatUrl: null, splatVersion: null, pose: normalizePose({}) } })
                liveTuneStore.setStateEphemeral({ environment: { assetId: null } })
                setSaveTune({ status: 'idle', msg: null })
                setDiffOpen(false)
                return
              }
              const picked = environments.find(en => en.url === url)
              set({ environment: {
                splatUrl: url,
                splatVersion: picked?.version ?? null,
                pose: normalizePose(picked?.defaultPose ?? {}),
              } })
              // assetId is server-derived, never a setting the user chose —
              // always ephemeral (see applyEnvironment.js), set directly from
              // the picked row rather than left to resolve asynchronously.
              liveTuneStore.setStateEphemeral({ environment: { assetId: picked?.id ?? null } })
              // Shared lighting: same tune-apply the content-browser path
              // (applyEnvironment) runs, so both activation paths agree.
              applyTuneForEnvironment(extractTuneFromAsset(picked))
              setSaveTune({ status: 'idle', msg: null })
              setDiffOpen(false)
            }}
            style={INPUT}
          >
            {/* If the current splat isn't in the list AND doesn't match any
                row by id either (URL-param override or stale localStorage
                from a previous character), surface it as "(custom)" so the
                dropdown reflects reality. */}
            {environment.splatUrl && !currentEnv && (
              <option value={environment.splatUrl}>{environment.splatUrl} (custom)</option>
            )}
            {/* Unloading is a first-class pick. Before this there was no way to
                get back to an empty scene from here at all — every option loaded
                something, so a heavy splat could be swapped but never dropped. */}
            <option value="">— none —</option>
            {environments.map(e => (
              <option key={e.url} value={e.url}>{e.label || e.url}</option>
            ))}
          </select>
        )}

        <div style={{ ...LABEL, marginTop: 6 }}>POSE (x / y / z)</div>
        <XYZ
          value={[environment.pose.px, environment.pose.py, environment.pose.pz]}
          onChange={([x, y, z]) => setPose('pos', { px: x, py: y, pz: z })}
        />
        <div style={{ ...LABEL, marginTop: 6 }}>ROTATION (rx / ry / rz)</div>
        <XYZ
          value={[environment.pose.rx, environment.pose.ry, environment.pose.rz]}
          onChange={([x, y, z]) => setPose('rot', { rx: x, ry: y, rz: z })}
        />
        <div style={{ ...LABEL, marginTop: 6 }}>SCALE</div>
        <LogSlider value={environment.pose.scale} min={ENV_SCALE_MIN} max={ENV_SCALE_MAX}
          onChange={v => setPose('scale', { scale: v })} />
        {/* ENG-895: was a linear slider capped at 10, which couldn't fit large-
            unit splats (Isaac OGS) at all. Now logarithmic to 1000, with the
            numeric field beside it for exact//out-of-range values. The
            content-browser inspector's Scale field remains uncapped. */}

        {/* Avatar floor plane. Blank = auto-detect from the splat; a value
            pins the character's feet so centered/offset exports don't bury
            her under the floor. */}
        <div style={{ ...LABEL, marginTop: 6 }}>FLOOR Y — AVATAR (blank = auto)</div>
        <input
          type="number"
          step={0.01}
          value={Number.isFinite(environment.pose.floorY) ? environment.pose.floorY : ''}
          placeholder="auto"
          onChange={e => {
            const raw = e.target.value
            set({ environment: { pose: { floorY: raw === '' ? null : parseFloat(raw) } } })
          }}
          style={INPUT}
        />

        <div style={{ ...ROW, marginTop: 6 }}>
          <button
            style={{ ...BTN, flex: 1, opacity: currentEnv ? 1 : 0.5,
                     cursor: currentEnv ? 'pointer' : 'not-allowed' }}
            disabled={!currentEnv || savePose.status === 'saving'}
            onClick={saveCurrentPoseAsDefault}
            title="Persist the current pose to this environment's default_pose on the server"
          >
            {savePose.status === 'saving' ? 'saving…' : 'Save pose as default'}
          </button>
        </div>
        {savePose.msg && (
          <div style={{ fontSize: 10, marginTop: 2,
                        color: savePose.status === 'error' ? 'var(--m-danger)' : 'var(--m-online)' }}>
            {savePose.msg}
          </div>
        )}
        </Section>

        {/* ── Backdrop — sky (scene background) + ground plane (ENG-894) ── */}
        <Section id="backdrop" modified={modifiedSections.has('backdrop')} title="BACKDROP" open={openSections.backdrop} filter={filterQuery} onToggle={toggleSection} accent={CATEGORY_COLORS.Scene} resetKey="backdrop">
          <div style={LABEL}>
            <Checkbox checked={backdrop.skyEnabled}
              onChange={v => set({ backdrop: { skyEnabled: v } })}
              label="SKY (BACKGROUND)" />
          </div>
          <div style={gatedStyle(backdrop.skyEnabled)}>
            <LabelTip style={{ marginTop: 4 }}
              tip="Fills the viewport behind everything as part of the rendered frame (unlike the themed canvas background, which is CSS only). Off = the app theme shows through.">
              BACKGROUND COLOR
            </LabelTip>
            <ColorInput value={backdrop.skyColor} testId="backdrop-sky-color"
              onChange={v => set({ backdrop: { skyColor: v } })} />
          </div>

          <div style={{ ...LABEL, marginTop: 8 }}>
            <Checkbox checked={backdrop.groundEnabled}
              onChange={v => set({ backdrop: { groundEnabled: v } })}
              label="GROUND PLANE" />
          </div>
          <div style={gatedStyle(backdrop.groundEnabled)}>
            <LabelTip style={{ marginTop: 4 }}
              tip="An opaque plane at the stage floor, so the avatar isn't floating when there's no environment. The shadow catcher composites on top of it — this plane doesn't receive shadows itself.">
              GROUND PLANE COLOR
            </LabelTip>
            <ColorInput value={backdrop.groundColor} testId="backdrop-ground-color"
              onChange={v => set({ backdrop: { groundColor: v } })} />
            <div style={{ ...LABEL, marginTop: 4 }}>GROUND SIZE (m)</div>
            <Slider value={backdrop.groundSize} min={2} max={60} step={1}
              onChange={v => set({ backdrop: { groundSize: v } })} />
          </div>

          {/* Only meaningful with an environment loaded, and that is exactly the
             case where the two controls above look broken: the sky is the scene
             background (behind ALL geometry) and the ground sits at the
             environment's own floor, so a room scan covers both. Offer the fix
             here rather than sending the user off to the ENVIRONMENT dropdown. */}
          {environment.splatUrl && (
            <>
              <div style={{ ...LABEL, marginTop: 10 }}>
                <Checkbox checked={backdrop.hideEnvironment}
                  onChange={v => set({ backdrop: { hideEnvironment: v } })}
                  label="HIDE ENVIRONMENT" />
              </div>
              <LocalNote>
                {backdrop.hideEnvironment
                  ? 'environment hidden — its lighting still applies'
                  : 'the loaded environment renders in front of the sky and over the ground plane'}
              </LocalNote>
            </>
          )}
        </Section>

        {/* ── Camera ─────────────────────────────────────────────────── */}
        {/* The section body covers the full camera model (see DEFAULTS.camera
            in liveTuneStore.js) as foldable SubGroups. Every control routes
            through setCam (setCameraFromPanel) so precedence/touched tracking
            works. */}
        <Section id="camera" modified={modifiedSections.has('camera')} title="CAMERA" open={openSections.camera} filter={filterQuery} onToggle={toggleSection} accent={CATEGORY_COLORS.Camera} resetKey="camera">
          <div style={LABEL}>CAMERA FOV</div>
          <Slider value={camera.fov} min={20} max={90} step={1}
            onChange={v => setCam({ fov: v })} />

          <CameraStatusChips camera={camera} />

          <SubGroup id="target" label="TARGET" open={openCameraSub.target} onToggle={toggleCameraSub} lockKey="camera.target">
            <div style={LABEL}>MODE</div>
            <select
              value={camera.target?.mode ?? 'fixed'}
              onChange={e => {
                const v = e.target.value
                if (v === 'vector') setCam({ target: { mode: 'vector', value: camera.target?.value ?? [0, 1.5, 0] } })
                else setCam({ target: { mode: v } })
              }}
              style={INPUT}
            >
              <option value="fixed">fixed</option>
              <option value="head">head</option>
              <option value="vector">vector</option>
            </select>
            {camera.target?.mode === 'vector' && (
              <>
                <div style={{ ...LABEL, marginTop: 4 }}>VECTOR (x / y / z)</div>
                <XYZ value={camera.target.value ?? [0, 1.5, 0]}
                  onChange={([x, y, z]) => setCam({ target: { mode: 'vector', value: [x, y, z] } })} />
              </>
            )}
          </SubGroup>

          <SubGroup id="cage" label="CAGE — box, offsettable from the avatar" open={openCameraSub.cage} onToggle={toggleCameraSub} lockKey="camera.cage">
            {camera.follow?.enabled && (
              <div style={{ color: 'var(--m-warn)', fontSize: 10, marginBottom: 4 }}>
                Follow active — cage not enforced.
              </div>
            )}
            {/* Deliberate exception to the usual gatedStyle 0.4: 0.6 keeps the
                cage numbers diagnostically legible while inert (a dev
                diagnosing a follow-mode framing issue still wants to read the
                last cage box), rather than nearly illegible. */}
            <div style={camera.follow?.enabled ? { opacity: 0.6, pointerEvents: 'none' } : undefined}>
              <Checkbox checked={camera.cage.enabled}
                onChange={v => setCam({ cage: { enabled: v } })}
                label="CAGE ENABLED" />
              <div style={{ ...LABEL, marginTop: 4 }}>HALF-EXTENTS (hx / hy / hz)</div>
              <XYZ step={0.1}
                value={[camera.cage.hx, camera.cage.hy, camera.cage.hz]}
                onChange={([hx, hy, hz]) => setCam({ cage: { hx, hy, hz } })} />
              <div style={{ fontSize: 10, color: 'var(--m-fg-3)', marginTop: 2 }}>
                box: {(2 * camera.cage.hx).toFixed(1)} × {(2 * camera.cage.hy).toFixed(1)} × {(2 * camera.cage.hz).toFixed(1)} m
              </div>
              <LabelTip style={{ marginTop: 4 }}
                tip="Moves the box away from the avatar — 0,0,0 centers it on the avatar. Deliberately not the orbit look-at point: panning the camera (right-drag) doesn't drag the box with it, and the box always constrains the camera regardless of where you're currently looking.">
                OFFSET FROM AVATAR (x / y / z)
              </LabelTip>
              <XYZ step={0.1}
                value={[camera.cage.ox ?? 0, camera.cage.oy ?? 0, camera.cage.oz ?? 0]}
                onChange={([ox, oy, oz]) => setCam({ cage: { ox, oy, oz } })} />
              <div style={{ ...LABEL, marginTop: 4 }}>MARGIN</div>
              <Slider value={camera.cage.margin} min={0} max={1.5} step={0.05}
                onChange={v => setCam({ cage: { margin: v } })} />
              <div style={{ marginTop: 6 }}>
                <Checkbox checked={cageGizmoVisible}
                  onChange={v => liveTuneStore.setCageGizmoVisible(v)}
                  label="Show in viewport" />
              </div>
              <LocalNote>shared: saved with "Save for everyone" and loaded with the environment</LocalNote>
            </div>
          </SubGroup>

          <SubGroup id="seeThrough" label="SEE-THROUGH" open={openCameraSub.seeThrough} onToggle={toggleCameraSub} lockKey="camera.seeThrough">
            <Checkbox checked={camera.seeThrough.enabled}
              onChange={v => setCam({ seeThrough: { enabled: v } })}
              label="ENABLED" />
            <div style={{ ...LABEL, marginTop: 4 }}>RADIUS</div>
            <Slider value={camera.seeThrough.radius} min={0} max={2} step={0.05}
              onChange={v => setCam({ seeThrough: { radius: v } })} />
            <div style={{ ...LABEL, marginTop: 4 }}>FADE</div>
            <Slider value={camera.seeThrough.fade} min={0} max={1} step={0.05}
              onChange={v => setCam({ seeThrough: { fade: v } })} />
            <div style={{ marginTop: 4 }}>
              <Checkbox checked={camera.seeThrough.auto}
                onChange={v => setCam({ seeThrough: { auto: v } })}
                label="AUTO" />
            </div>
            <div style={{ ...LABEL, marginTop: 4 }}>AUTO NEAR</div>
            <Slider value={camera.seeThrough.autoNear} min={0} max={5} step={0.1}
              onChange={v => setCam({ seeThrough: { autoNear: v } })} />
            <div style={{ marginTop: 4 }}>
              <Checkbox checked={camera.seeThrough.autoBelow}
                onChange={v => setCam({ seeThrough: { autoBelow: v } })}
                label="AUTO BELOW" />
            </div>
          </SubGroup>

          <SubGroup id="follow" label="FOLLOW" open={openCameraSub.follow} onToggle={toggleCameraSub} lockKey="camera.follow">
            <Checkbox checked={camera.follow.enabled}
              onChange={v => setCam({ follow: { enabled: v } })}
              label="ENABLED" />
            <div style={{ ...LABEL, marginTop: 4 }}>OFFSET (x / y / z)</div>
            <XYZ value={camera.follow.offset} step={0.05}
              onChange={([x, y, z]) => setCam({ follow: { offset: [x, y, z] } })} />
            <div style={{ ...LABEL, marginTop: 4 }}>SPACE</div>
            <select value={camera.follow.space} onChange={e => setCam({ follow: { space: e.target.value } })} style={INPUT}>
              <option value="local">local</option>
              <option value="world">world</option>
            </select>
            <div style={{ ...LABEL, marginTop: 4 }}>DAMPING</div>
            <Slider value={camera.follow.damping} min={0} max={1} step={0.01}
              onChange={v => setCam({ follow: { damping: v } })} />
            <div style={{ ...LABEL, marginTop: 4 }}>LOOK Y+</div>
            <Slider value={camera.follow.targetOffsetY} min={-2} max={2} step={0.05}
              onChange={v => setCam({ follow: { targetOffsetY: v } })} />
          </SubGroup>

          <SubGroup id="blend" label="BLEND" open={openCameraSub.blend} onToggle={toggleCameraSub} lockKey="camera.blend">
            <div style={LABEL}>FOV (ms)</div>
            <Slider value={camera.blend.fov} min={0} max={2000} step={25}
              onChange={v => setCam({ blend: { fov: v } })} />
            <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
              {[0, 150, 300, 600].map(n => (
                <button key={n} type="button" onClick={() => setCam({ blend: { fov: n } })}
                  style={{ ...BTN, padding: '2px 6px', fontSize: 10 }}>{n}</button>
              ))}
            </div>
            <div style={{ ...LABEL, marginTop: 6 }}>TARGET (ms)</div>
            <Slider value={camera.blend.target} min={0} max={2000} step={25}
              onChange={v => setCam({ blend: { target: v } })} />
            <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
              {[0, 150, 300, 600].map(n => (
                <button key={n} type="button" onClick={() => setCam({ blend: { target: n } })}
                  style={{ ...BTN, padding: '2px 6px', fontSize: 10 }}>{n}</button>
              ))}
            </div>
            <LocalNote>local — the duration of whatever move is CURRENTLY in flight, not part of the shared look (unlike TARGET / CAGE / SEE-THROUGH / FOLLOW above)</LocalNote>
          </SubGroup>
        </Section>

        {/* ── Shadow: the shared catcher plane ONLY. Opacity + map resolution
             are the "global" knobs (the plane everything casts onto — SHADOW
             OPACITY is the plane's darkness; MAP RESOLUTION here is the sun's
             own shadow-map, kept alongside it by request rather than moved
             into LIGHTING — DIRECTIONAL below). Everything else about
             the directional light itself lives in that section instead — both
             still edit the same `shadow` store slice (see sectionOfKey /
             resetKey="shadow" on both). */}
        <Section id="shadow" modified={modifiedSections.has('shadow')} title="SHADOW" open={openSections.shadow} filter={filterQuery} onToggle={toggleSection} accent={CATEGORY_COLORS.Light} resetKey="shadow">
          <div style={SUBHEADER}>CATCHER PLANE</div>
          <LabelTip
            tip="One catcher plane fakes ground shadows for every light (splats can't receive shadows). Each light's shadow only shows while that light is on — toggle the sun or a spotlight to include/exclude its shadow. This opacity sets the darkness of the shared plane.">
            SHADOW OPACITY
          </LabelTip>
          <Slider value={shadow.opacity} min={0} max={1} step={0.02}
            onChange={v => set({ shadow: { opacity: v } })} />
          <div style={{ ...LABEL, marginTop: 4 }}>FLOOR Y (blank = auto-detect)</div>
          <div style={ROW}>
            <input
              type="number" step={0.01}
              value={shadow.floorYOverride ?? ''}
              placeholder="auto"
              onChange={e => {
                const s = e.target.value
                if (s === '') set({ shadow: { floorYOverride: null } })
                else {
                  const v = parseFloat(s)
                  if (Number.isFinite(v)) set({ shadow: { floorYOverride: v } })
                }
              }}
              style={INPUT}
            />
          </div>
          <div style={{ ...LABEL, marginTop: 8 }}>MAP RESOLUTION</div>
          <select
            value={shadow.mapSize}
            onChange={e => set({ shadow: { mapSize: parseInt(e.target.value, 10) } })}
            style={INPUT}
          >
            <option value={1024}>1024 — low</option>
            <option value={2048}>2048 — medium</option>
            <option value={4096}>4096 — high</option>
          </select>
        </Section>

        {/* ── Lighting — Directional (the shadow-casting "sun"). Split
             out of SHADOW so the catcher-plane globals and the light's own
             knobs aren't crammed into one section; still the same `shadow`
             store slice underneath. */}
        <Section id="lightingDirectional" modified={modifiedSections.has('lightingDirectional')} title="LIGHTING — DIRECTIONAL" open={openSections.lightingDirectional} filter={filterQuery} onToggle={toggleSection} accent={CATEGORY_COLORS.Light} resetKey="shadow">
          <div style={LABEL}>
            <Checkbox checked={shadow.enabled}
              onChange={v => set({ shadow: { enabled: v } })}
              label="DIRECTIONAL" />
          </div>
          <div style={gatedStyle(shadow.enabled)}>
          <div style={{ ...LABEL, marginTop: 4 }}>LIGHT INTENSITY</div>
          <Slider value={shadow.lightIntensity} min={0} max={2} step={0.05}
            onChange={v => set({ shadow: { lightIntensity: v } })} />
          <div style={{ ...LABEL, marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>LIGHT TEMPERATURE (K)</span>
            <span style={{ width: 14, height: 14, borderRadius: 2, border: '1px solid var(--m-line-hi)',
                           background: kelvinToHex(shadow.temperature) }} />
          </div>
          <Slider value={shadow.temperature} min={1000} max={12000} step={50}
            onChange={v => set({ shadow: { temperature: v } })} />
          <div style={{ ...LABEL, marginTop: 4 }}>BLUR RADIUS</div>
          <Slider value={shadow.radius} min={0} max={40} step={1}
            onChange={v => set({ shadow: { radius: v } })} />
          <div style={{ ...LABEL, marginTop: 4 }}>BLUR SAMPLES</div>
          <Slider value={shadow.blurSamples} min={1} max={100} step={1}
            onChange={v => set({ shadow: { blurSamples: v } })} />
          <div style={{ ...LABEL, marginTop: 4 }}>FRUSTUM SIZE (m, tighter = crisper)</div>
          <Slider value={shadow.cameraSize} min={1} max={8} step={0.1}
            onChange={v => set({ shadow: { cameraSize: v } })} />
          <LabelTip style={{ marginTop: 4 }}
            tip="Banding: raise MAP RESOLUTION (SHADOW section) and tighten FRUSTUM SIZE around the avatar for more shadow texels; nudge BIAS if you see light-bleed or contact-detachment.">
            SUN SHADOW BIAS
          </LabelTip>
          <Slider value={shadow.bias} min={-0.005} max={0.001} step={0.0001}
            onChange={v => set({ shadow: { bias: v } })} />
          <div style={{ ...LABEL, marginTop: 4 }}>LIGHT ELEVATION (° above horizon)</div>
          <Slider value={shadow.lightElevation} min={5} max={89} step={1}
            onChange={v => set({ shadow: { lightElevation: v } })} />
          <div style={{ ...LABEL, marginTop: 4 }}>LIGHT AZIMUTH (° around character)</div>
          <Slider value={shadow.lightAzimuth} min={-180} max={180} step={1}
            onChange={v => set({ shadow: { lightAzimuth: v } })} />
          <div style={{ ...LABEL, marginTop: 4 }}>LIGHT DISTANCE (m)</div>
          <Slider value={shadow.lightDistance} min={1} max={15} step={0.1}
            onChange={v => set({ shadow: { lightDistance: v } })} />
          </div>
        </Section>

        {/* ── Lighting ───────────────────────────────────────────────── */}
        <Section id="lightingEnvironment" modified={modifiedSections.has('lightingEnvironment')} title="LIGHTING — ENVIRONMENT" open={openSections.lightingEnvironment} filter={filterQuery} onToggle={toggleSection} accent={CATEGORY_COLORS.Light} resetKey="lighting">
          <div style={LABEL}>
            <Checkbox checked={lighting.iblEnabled}
              onChange={v => set({ lighting: { iblEnabled: v } })}
              label="IBL" />
          </div>
          <div style={gatedStyle(lighting.iblEnabled)}>
          <div style={{ ...LABEL, marginTop: 4 }}>envMapIntensity</div>
          <Slider value={lighting.envMapIntensity} min={0} max={5} step={0.05}
            onChange={v => set({ lighting: { envMapIntensity: v } })} />
          </div>
          <LabelTip style={{ marginTop: 4 }}
            tip="IBL supplies soft fill; the KEY LIGHT is an analytic directional layered on top so normal maps catch light (pores/wrinkles/weave). Its direction follows the DIRECTIONAL sun sliders. 0 = pure IBL. With IBL off, the edit-mode 3-light rig is shown as a fallback.">
            KEY LIGHT
          </LabelTip>
          <Slider value={lighting.keyLightIntensity ?? 1.2} min={0} max={4} step={0.05}
            onChange={v => set({ lighting: { keyLightIntensity: v } })} />
          <div style={{ ...LABEL, marginTop: 8 }}>AMBIENT</div>
          <Slider value={lighting.ambientIntensity} min={0} max={2} step={0.05}
            onChange={v => set({ lighting: { ambientIntensity: v } })} />
          <div style={{ ...LABEL, marginTop: 4 }}>AMBIENT COLOR</div>
          <ColorInput value={lighting.ambientColor}
            onChange={v => set({ lighting: { ambientColor: v } })} />
          <div style={{ ...LABEL, marginTop: 8 }}>
            <Checkbox checked={lighting.lightProbeEnabled}
              onChange={v => set({ lighting: { lightProbeEnabled: v } })}
              label="LIGHT PROBE" />
          </div>
          <div style={gatedStyle(lighting.lightProbeEnabled)}>
          <LabelTip style={{ marginTop: 4 }}
            tip="Baked-splat diffuse SH ambient bounce, position-driven. Independent of IBL — set to 0 (or uncheck above) to fully remove its contribution.">
            PROBE INTENSITY
          </LabelTip>
          <Slider value={lighting.lightProbeIntensity} min={0} max={2} step={0.05}
            onChange={v => set({ lighting: { lightProbeIntensity: v } })} />
          </div>
        </Section>

        {/* ── Lighting — Spot (orbit rig, aims at a target on the Y
             axis). Slot 1 is always present (matches the original single
             spotlight exactly — same DOM, same globals). "+ ADD SPOT"
             reveals up to 2 more, e.g. to match the studio rig's shadow-
             casting lights with real, individually-aimable shadows. */}
        <Section id="lightingSpotlight" modified={modifiedSections.has('lightingSpotlight')} title="LIGHTING — SPOT" open={openSections.lightingSpotlight} filter={filterQuery} onToggle={toggleSection} accent={CATEGORY_COLORS.Light} resetKey="spotlight">
          <SpotlightControls v={spotlight} onSet={patch => set({ spotlight: patch })} checkboxLabel="SPOT" />

          {revealedSpotSlots[2] && (
            <>
              <div style={SUBDIVIDER} />
              <SpotlightControls v={spotlight2} onSet={patch => set({ spotlight2: patch })} checkboxLabel="SPOT 2"
                onRemove={() => {
                  set({ spotlight2: { enabled: false } })
                  setRevealedSpotSlots(s => ({ ...s, 2: false }))
                }} />
            </>
          )}
          {revealedSpotSlots[3] && (
            <>
              <div style={SUBDIVIDER} />
              <SpotlightControls v={spotlight3} onSet={patch => set({ spotlight3: patch })} checkboxLabel="SPOT 3"
                onRemove={() => {
                  set({ spotlight3: { enabled: false } })
                  setRevealedSpotSlots(s => ({ ...s, 3: false }))
                }} />
            </>
          )}
          {(!revealedSpotSlots[2] || !revealedSpotSlots[3]) && (
            <button
              type="button"
              data-testid="spotlight-add"
              style={{ ...BTN, marginTop: 10, width: '100%' }}
              onClick={() => {
                if (!revealedSpotSlots[2]) {
                  setRevealedSpotSlots(s => ({ ...s, 2: true }))
                  set({ spotlight2: { enabled: true } })
                } else {
                  setRevealedSpotSlots(s => ({ ...s, 3: true }))
                  set({ spotlight3: { enabled: true } })
                }
              }}
            >
              + ADD SPOT ({1 + (revealedSpotSlots[2] ? 1 : 0) + (revealedSpotSlots[3] ? 1 : 0)}/3)
            </button>
          )}
        </Section>

        {/* ── Lighting — Hemisphere (sky/ground ambient fill) ──────────── */}
        <Section id="lightingHemisphere" modified={modifiedSections.has('lightingHemisphere')} title="LIGHTING — HEMISPHERE" open={openSections.lightingHemisphere} filter={filterQuery} onToggle={toggleSection} accent={CATEGORY_COLORS.Light} resetKey="hemisphere">
          <div style={LABEL}>
            <Checkbox checked={hemisphere.enabled}
              onChange={v => set({ hemisphere: { enabled: v } })}
              label="HEMISPHERE" />
          </div>
          <div style={gatedStyle(hemisphere.enabled)}>
          <div style={{ ...LABEL, marginTop: 8 }}>SKY COLOR</div>
          <ColorInput value={hemisphere.skyColor}
            onChange={v => set({ hemisphere: { skyColor: v } })} />
          <div style={{ ...LABEL, marginTop: 4 }}>SKY INTENSITY</div>
          <Slider value={hemisphere.skyIntensity} min={0} max={3} step={0.05}
            onChange={v => set({ hemisphere: { skyIntensity: v } })} />
          <div style={{ ...LABEL, marginTop: 8 }}>GROUND COLOR</div>
          <ColorInput value={hemisphere.groundColor}
            onChange={v => set({ hemisphere: { groundColor: v } })} />
          <div style={{ ...LABEL, marginTop: 4 }}>GROUND INTENSITY</div>
          <Slider value={hemisphere.groundIntensity} min={0} max={3} step={0.05}
            onChange={v => set({ hemisphere: { groundIntensity: v } })} />
          </div>
        </Section>

        {/* ── Movable torch (point light + emitter cube). Section id/resetKey
             stay "torch" (matches the store slice + window.__TORCH__, which
             CLAUDE.md forbids renaming) — only the displayed title changed. */}
        <Section id="torch" modified={modifiedSections.has('torch')} title="LIGHTING — POINT" open={openSections.torch} filter={filterQuery} onToggle={toggleSection} accent={CATEGORY_COLORS.Light} resetKey="torch">
          <div style={LABEL}>
            <Checkbox checked={torch.enabled}
              onChange={v => set({ torch: { enabled: v } })}
              label="POINT" />
          </div>
          <div style={gatedStyle(torch.enabled)}>
            <div style={{ ...LABEL, marginTop: 8 }}>COLOR</div>
            <ColorInput value={torch.color} onChange={v => set({ torch: { color: v } })} testId="torch-color" />
            <LabelTip style={{ marginTop: 6 }}
              tip="Candela (decay 2), so intensity and distance are tied — halve the distance and it lands 4x brighter.">
              INTENSITY
            </LabelTip>
            <Slider value={torch.intensity} min={0} max={12} step={0.1}
              onChange={v => set({ torch: { intensity: v } })} />
            <LabelTip style={{ marginTop: 4 }}
              tip="World Y the torch orbits — set it to head height so elevation/azimuth read as angles about the face.">
              AIM HEIGHT (m)
            </LabelTip>
            <Slider value={torch.targetHeight} min={0} max={2.5} step={0.01}
              onChange={v => set({ torch: { targetHeight: v } })} />
            <div style={{ ...LABEL, marginTop: 4 }}>DISTANCE (m)</div>
            <Slider value={torch.distance} min={0.1} max={4} step={0.01}
              onChange={v => set({ torch: { distance: v } })} />
            <div style={{ ...LABEL, marginTop: 4 }}>ELEVATION (°)</div>
            <Slider value={torch.elevation} min={-90} max={90} step={1}
              onChange={v => set({ torch: { elevation: v } })} />
            <div style={{ ...LABEL, marginTop: 4 }}>AZIMUTH (°)</div>
            <Slider value={torch.azimuth} min={-180} max={180} step={1}
              onChange={v => set({ torch: { azimuth: v } })} />
            <div style={{ ...LABEL, marginTop: 8 }}>
              <Checkbox checked={torch.cube}
                onChange={v => set({ torch: { cube: v } })}
                label="SHOW EMITTER CUBE" />
            </div>
            <div style={gatedStyle(torch.cube)}>
              <div style={{ ...LABEL, marginTop: 4 }}>CUBE SIZE (m)</div>
              <Slider value={torch.cubeSize} min={0.01} max={0.4} step={0.005}
                onChange={v => set({ torch: { cubeSize: v } })} />
            </div>
            <div style={{ ...LABEL, marginTop: 8 }}>
              <Checkbox checked={torch.followCamera}
                onChange={v => set({ torch: { followCamera: v } })}
                label="RIDE CAMERA (HEADLAMP)" />
            </div>
            <button
              data-testid="torch-place-at-camera"
              onClick={placeTorchAtCamera}
              title="Put the torch on the view axis just in front of the camera and select it, so there's a gizmo to drag. Frame the subject first."
              style={{ ...BTN, width: '100%', marginTop: 8 }}>
              {torch.followCamera ? 'DROP IT HERE' : 'PLACE AT CAMERA'}
            </button>
            <div style={{ color: 'var(--m-fg-3)', fontSize: 10, lineHeight: 1.4, marginTop: 6 }}>
              {torch.followCamera
                ? 'On the camera — orbit to sweep the light. Placement stands down, and the outliner row goes with it; DROP IT HERE lets go and leaves it where you are looking from.'
                : 'PLACE AT CAMERA brings it into view and picks it — then drag the gizmo, or sweep AZIMUTH. Enabling the torch stands down edit mode’s three fixed lights so it is the actual source.'}
            </div>
          </div>
        </Section>

        {/* ── PBR (only when active character uses MeshStandardMaterial) ─ */}
        {!isToonChar && (
          <Section id="pbr" modified={modifiedSections.has('pbr')} title="PBR — CLOTH / TEETH / ACCESSORIES" open={openSections.pbr} filter={filterQuery} onToggle={toggleSection} accent={CATEGORY_COLORS.Material} resetKey="pbr">
            <div style={{ ...LABEL, marginTop: 4 }}>ROUGHNESS (× authored)</div>
            <Slider value={pbr?.roughness ?? 1.0} min={0} max={2} step={0.01}
              onChange={v => set({ pbr: { roughness: v } })} />
            <div style={{ ...LABEL, marginTop: 4 }}>METALNESS (× authored)</div>
            <Slider value={pbr?.metalness ?? 1.0} min={0} max={2} step={0.01}
              onChange={v => set({ pbr: { metalness: v } })} />
          </Section>
        )}

        {/* ── Skin (per-role MeshPhysicalMaterial; body + face) ─────── */}
        {!isToonChar && (
          <Section id="skin" modified={modifiedSections.has('skin')} title="SKIN MATERIAL" open={openSections.skin} filter={filterQuery} onToggle={toggleSection} accent={CATEGORY_COLORS.Material} resetKey="pbrSkin">
            <LabelTip style={{ marginTop: 4 }}
              tip="Global multipliers over each material's authored value (1.0 = untouched). Per-material Normal Strength / roughness live in the Content browser's Material inspector; these scale on top of it.">
              ROUGHNESS (× authored)
            </LabelTip>
            <Slider value={pbrSkin?.roughness ?? 1.0} min={0} max={2} step={0.01}
              onChange={v => set({ pbrSkin: { roughness: v } })} />
            <div style={{ ...LABEL, marginTop: 4 }}>NORMAL SCALE (× authored)</div>
            <Slider value={pbrSkin?.normalScale ?? 1.0} min={0} max={3} step={0.05}
              onChange={v => set({ pbrSkin: { normalScale: v } })} />
            <div style={{ ...LABEL, marginTop: 4 }}>THICKNESS (× authored)</div>
            <Slider value={pbrSkin?.thickness ?? 1.0} min={0} max={2} step={0.05}
              onChange={v => set({ pbrSkin: { thickness: v } })} />
          </Section>
        )}

        {/* ── Debug ──────────────────────────────────────────────────── */}
        <Section id="debug" title="DEBUG" open={openSections.debug} filter={filterQuery} onToggle={toggleSection} accent={CATEGORY_COLORS.Debug} resetKey="debug">
          <Checkbox checked={debug.mirrorBall}
            onChange={v => set({ debug: { mirrorBall: v } })}
            label="MIRROR BALL" />
          <Checkbox checked={debug.probeSphere}
            onChange={v => set({ debug: { probeSphere: v } })}
            label="PROBE SPHERE" />
          <LocalNote>dev oracles — local to this browser, never shared</LocalNote>
          <div style={{ color: 'var(--m-fg-3)', fontSize: 10, marginTop: 8, lineHeight: 1.4 }}>
            <div>IBL state: <span style={{ color: 'var(--m-fg-2)' }}>{envProbe?.state ?? '—'}</span></div>
            {envProbe?.key && <div>hash: <span style={{ color: 'var(--m-fg-2)' }}>{envProbe.key}</span></div>}
            {envProbe?.state === 'no-cache' && envProbe?.cacheUrl && (
              <div style={{ color: 'var(--m-warn)' }}>
                bake: <code style={{ fontSize: 10 }}>npm run bake-ibl -- {environment.splatUrl}</code>
              </div>
            )}
          </div>
        </Section>

      </div>}

      {/* ── Pinned footer — the single commit surface ─────────────────────
         Sits outside the scroll area so Save/Discard are reachable from any
         scroll position. Quiet when in sync; grows the change list + actions
         only while something is unsaved. */}
      {!collapsed && (
        <div data-testid="tune-footer" style={{ borderTop: '1px solid var(--m-line-hi)', background: 'var(--m-panel)', flexShrink: 0 }}>
          {currentEnv && !tuneIsDirty && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', fontSize: 10, color: 'var(--m-fg-3)' }}>
              <span style={{ color: 'var(--m-online)' }}>✓</span>
              {saveTune.status === 'saved' ? 'saved — everyone gets this look' : 'matches the shared look — edits appear here'}
            </div>
          )}

          {!currentEnv && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', fontSize: 10, color: 'var(--m-fg-3)' }}>
              <span style={{ color: 'var(--m-fg-3)' }}>○</span>
              no environment loaded — a shared look is saved onto one
            </div>
          )}

          {currentEnv && tuneIsDirty && (
            <>
              <div
                data-testid="tune-diff-toggle"
                role="button"
                onClick={() => setDiffOpen(o => !o)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', fontSize: 11, cursor: 'pointer', userSelect: 'none' }}
              >
                <span style={{ color: 'var(--m-warn)', fontWeight: 700 }}>{tuneDiff.length}</span>
                <span>setting{tuneDiff.length > 1 ? 's' : ''} differ{tuneDiff.length > 1 ? '' : 's'} from the shared look</span>
                <span style={{ marginLeft: 'auto', color: 'var(--m-fg-3)' }}>{diffOpen ? '▴' : '▾'}</span>
              </div>

              {diffOpen && (
                <div data-testid="tune-diff-list" style={{ maxHeight: 150, overflowY: 'auto', borderTop: '1px solid var(--m-line)62c' }}>
                  {tuneDiff.map(d => (
                    <div key={d.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 10px', fontSize: 10, borderTop: '1px solid var(--m-line)' }}>
                      <span style={{ flex: 1, color: 'var(--m-fg-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.key}</span>
                      <span style={{ color: 'var(--m-fg-3)', textDecoration: 'line-through' }}>{fmtTuneValue(d.from)}</span>
                      <span style={{ color: 'var(--m-fg-3)' }}>→</span>
                      <span style={{ color: 'var(--m-warn)' }}>{fmtTuneValue(d.to)}</span>
                      <button
                        title="Revert this setting to the shared look"
                        onClick={() => revertTuneKey(d.key)}
                        style={{ background: 'none', border: '1px solid var(--m-line-hi)', color: 'var(--m-fg-3)', borderRadius: 3, fontFamily: 'var(--m-font)', fontSize: 10, padding: '0 6px', cursor: 'pointer' }}
                      >↺</button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Save/Discard ALWAYS render. They used to mount only while
             `currentEnv && tuneIsDirty`, so on the common states — no
             environment loaded, or nothing changed yet — the commit surface
             was simply absent, and "where is Save for everyone?" had no
             answer on screen. A disabled control that says why beats a
             control that isn't there (same rule as the rig-problem pill and
             the preset buttons: never a silent no-op). */}
          <div style={{ display: 'flex', gap: 8, padding: '8px 10px 4px' }}>
            <button
              data-testid="tune-save-btn"
              disabled={!saveEnabled}
              title={saveDisabledReason ?? 'Publish this lighting to the environment so teammates get it'}
              onClick={saveCurrentTuneForEveryone}
              style={{
                ...BTN, flex: 1, fontWeight: 600, padding: '6px 10px',
                background: 'color-mix(in srgb, var(--m-online) 10%, var(--m-panel))', border: '1px solid color-mix(in srgb, var(--m-online) 53%, transparent)', color: 'color-mix(in srgb, var(--m-online) 45%, var(--m-fg))',
                opacity: saveEnabled ? 1 : 0.45,
                cursor: saveEnabled ? 'pointer' : 'not-allowed',
              }}
            >
              {saveTune.status === 'saving' ? 'saving…' : 'Save for everyone'}
            </button>
            <button
              data-testid="tune-discard-btn"
              disabled={!tuneIsDirty}
              title={tuneIsDirty ? 'Revert every knob to the shared look — nothing is sent' : 'Nothing to discard'}
              onClick={() => { discardTuneToBaseline(); setDiffOpen(false); setSaveTune({ status: 'idle', msg: null }) }}
              style={{ ...BTN, padding: '6px 10px', opacity: tuneIsDirty ? 1 : 0.45, cursor: tuneIsDirty ? 'pointer' : 'not-allowed' }}
            >Discard</button>
          </div>
          <div style={{ padding: '2px 10px 8px', fontSize: 9, color: 'var(--m-fg-3)' }}>
            {saveDisabledReason ?? (
              <>
                Save publishes to <span style={{ color: 'var(--m-online)' }}>{currentEnv.label}</span>
                {tuneNeverSaved ? ' (no shared look saved yet)' : ''} — teammates get it when they load this environment.
              </>
            )}
          </div>
          {saveTune.status === 'error' && saveTune.msg && (
            <div style={{ padding: '0 10px 8px', fontSize: 10, color: 'var(--m-danger)' }}>{saveTune.msg}</div>
          )}

          {/* Utility row — unchanged behaviour, relocated from the old
             bottom Actions section. The Save/Discard row above always renders
             now, so this no longer needs its own top padding for the
             no-environment case (and mixing `padding` with `paddingTop` across
             renders is what React warns about). */}
          <div style={{ display: 'flex', gap: 8, padding: '0 10px 8px' }}>
            <button onClick={copyShareUrl} style={{ ...BTN, flex: 1, fontSize: 10 }}>{copied ? 'Copied ✓' : 'Copy URL'}</button>
            <button onClick={() => { if (window.confirm('Reset all live-tune settings?')) liveTuneStore.reset() }}
              style={{ ...BTN, flex: 1, fontSize: 10 }}>Reset all</button>
          </div>
        </div>
      )}
    </div>
  )
}
